import {Button, Col, Row, Spin, Table} from 'antd';
import {Link, useHistory} from 'react-router-dom';
import Title from 'antd/es/typography/Title';
import moment from 'moment';
import {useEffect, useState} from 'react';
import Text from 'antd/lib/typography/Text';
import {
  PaginatedSchoolsResponse,
  SchoolProfile,
} from '@PikaDoApp/npm-pikado-logic';

import {SchoolService} from './SchoolService';
import {SchoolImportModal} from './SchoolImportModal';
import {getCurrentUserPermission, Permissions} from '../../utils';
import {useTablePagination} from '../../hooks/useTablePagination';

const schoolService = new SchoolService();

export const SchoolsTable = () => {
  const history = useHistory();

  const [schools, setSchools] = useState<PaginatedSchoolsResponse>();
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isPermissionResolved, setIsPermissionResolved] =
    useState<boolean>(false);
  const [currentUserPermission, setCurrentUserPermission] =
    useState<Permissions>();
  const [isImportModalOpen, setIsImportModalOpen] = useState<boolean>(false);

  const {current, pageSize, setTotal, paginationConfig, paginationSelector} =
    useTablePagination({
      itemName: 'schools',
      defaultPageSize: 100,
    });

  useEffect(() => {
    getCurrentUserPermission()
      .then((p: Permissions) => {
        setCurrentUserPermission(p);
        setIsPermissionResolved(true);
      })
      .catch(() => {
        setIsPermissionResolved(true);
        setIsLoading(false);
        setIsError(true);
      });
  }, []);

  useEffect(() => {
    if (!isPermissionResolved) {
      return;
    }

    setIsLoading(true);
    const offset = (current - 1) * pageSize;

    schoolService
      .getAllSchools(pageSize, offset)
      .then(resp => {
        setSchools(resp);
        setTotal(resp?.totalCount ?? 0);
        setIsLoading(false);
        setIsError(false);
      })
      .catch(() => {
        setIsError(true);
        setIsLoading(false);
      });
  }, [
    current,
    pageSize,
    currentUserPermission,
    isPermissionResolved,
    setTotal,
  ]);

  const columns: any[] = [
    {
      title: 'Name English',
      dataIndex: 'name',
      render: (name: string, school: any) => (
        <Link to={'/school/' + school.id + '/edit'}>{name}</Link>
      ),
    },
    {
      title: 'Name Arabic',
      dataIndex: 'nameArabic',
      render: (name: string) => (
        <Text style={{textAlign: 'center', fontWeight: 'bold'}}>{name}</Text>
      ),
    },
    {
      title: 'Created at',
      dataIndex: 'createdAt',
      render: (createdAt: string) => (
        <p style={{textAlign: 'left', fontWeight: 'bold'}}>
          {moment(createdAt).format('YYYY-MM-DD')}
        </p>
      ),
    },
    {
      title: 'ID',
      dataIndex: 'id',
      render: (id: string) => (
        <p style={{textAlign: 'left', fontWeight: 'bold'}}>{id}</p>
      ),
    },
  ];

  return (
    <>
      <Row
        style={{
          margin: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
        <Col />
        <Col style={{display: 'flex', gap: 12, alignItems: 'center'}}>
          {paginationSelector}
          {currentUserPermission === 'admin' && !isLoading && (
            <>
              <Button size="large" onClick={() => setIsImportModalOpen(true)}>
                Import .xlsx
              </Button>
              <Button
                size="large"
                onClick={() => history.push('/schools/create')}>
                Create
              </Button>
            </>
          )}
        </Col>
      </Row>

      {!isError && !isLoading && (
        <Table
          rowKey={(record: SchoolProfile & {id?: string}) =>
            (record as any).id ?? record._id
          }
          pagination={paginationConfig}
          columns={columns}
          dataSource={schools?.items}
        />
      )}

      {isLoading && (
        <Spin size="large" style={{margin: 'auto', width: '100%'}} />
      )}
      {isError && <Title>Schools fetching error</Title>}

      <SchoolImportModal
        open={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
      />
    </>
  );
};
